import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, Gift, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GiftSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let attempt = 0;

    async function poll() {
      if (cancelled) return;
      if (attempt >= 10) {
        setStatus("timeout");
        return;
      }
      attempt += 1;
      try {
        const res = await axios.get(`${API}/gifts/checkout/status/${sessionId}`);
        if (cancelled) return;
        setData(res.data);
        if (res.data.payment_status === "paid" && res.data.voucher_code) {
          setStatus("paid");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    }
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  function copyCode() {
    if (data?.voucher_code) {
      navigator.clipboard.writeText(data.voucher_code);
      toast.success("Code copied.");
    }
  }

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center px-6" data-testid="gift-success-page">
      <div className="w-full max-w-xl bg-white border border-[color:var(--am-line)] p-12 text-center">
        <div className="flex justify-center mb-8">
          {status === "checking" && <Loader2 size={28} className="text-navy animate-spin" />}
          {status === "paid" && (
            <div className="w-14 h-14 bg-navy text-white flex items-center justify-center">
              <Gift size={24} strokeWidth={1.5} />
            </div>
          )}
          {(status === "error" || status === "timeout") && (
            <div className="w-14 h-14 bg-pink-soft text-navy flex items-center justify-center">
              <Check size={24} strokeWidth={1.5} />
            </div>
          )}
        </div>

        {status === "checking" && (
          <>
            <div className="overline mb-3">Confirming</div>
            <h1 className="font-serif-display text-4xl text-navy mb-4">Wrapping your gift…</h1>
            <p className="text-[color:var(--am-muted)]">Generating the certificate and sending it now.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="overline mb-3">Gift Sent</div>
            <h1 className="font-serif-display text-4xl text-navy mb-4">Beautifully done.</h1>
            <p className="text-[color:var(--am-muted)] max-w-md mx-auto">
              The recipient just received a branded PDF gift certificate and the redemption code by email. A receipt is on its way to your inbox too.
            </p>
            {data?.voucher_code && (
              <div className="mt-8 bg-pink-soft p-6">
                <div className="overline mb-2">Voucher Code</div>
                <div className="flex items-center justify-center gap-3">
                  <span className="font-mono text-2xl text-navy tracking-widest" data-testid="gift-success-code">
                    {data.voucher_code}
                  </span>
                  <button
                    onClick={copyCode}
                    className="text-navy/70 hover:text-navy"
                    aria-label="Copy code"
                    data-testid="gift-copy-code"
                  >
                    <Copy size={16} />
                  </button>
                </div>
                <div className="text-xs text-[color:var(--am-muted)] mt-2">
                  Saved in your receipt email · valid for 1 year
                </div>
              </div>
            )}
          </>
        )}
        {status === "timeout" && (
          <>
            <div className="overline mb-3">Still Processing</div>
            <h1 className="font-serif-display text-4xl text-navy mb-4">Almost there.</h1>
            <p className="text-[color:var(--am-muted)]">Your payment is settling. The certificate will arrive by email within a few minutes.</p>
          </>
        )}
        {status === "error" && (
          <>
            <div className="overline mb-3">Missing Session</div>
            <h1 className="font-serif-display text-4xl text-navy mb-4">No purchase found.</h1>
            <p className="text-[color:var(--am-muted)]">If you just paid, the gift email is on its way.</p>
          </>
        )}

        <div className="flex justify-center gap-3 mt-10">
          <Link to="/" className="btn-secondary" data-testid="gift-success-home">Home</Link>
          <Link to="/gift" className="btn-primary" data-testid="gift-success-another">Send Another</Link>
        </div>
      </div>
    </div>
  );
}
